import {
  CandidateDetails,
  ExamItem,
  SelectedCountry,
} from "../../types/common";

export type EventParams =
  | Gtag.ControlParams
  | Gtag.EventParams
  | Gtag.CustomParams;

export enum GT_EVENT {
  PageView = "page_view",
  SelectContent = "select_content",
  ViewItemList = "view_item_list",
  AddToCart = "add_to_cart",
  BeginCheckout = "begin_checkout",
  AddShippingInfo = "add_shipping_info",
  AddPaymentInfo = "add_payment_info",
  Purchase = "purchase",
}

const mapExamToItem = (exam: ExamItem) => ({
  item_id: exam.examId,
  item_name: exam.examName,
  item_brand: "Cambridge",
  item_category: exam.examFormat,
  item_category2: exam.speakingTestDate ? "Speaking Test" : "",
  item_category3: exam.isSchoolExam ? "School Exam" : "",
  item_category4: Number(exam.lateFeePrice) !== 0 ? "Late Fee Added" : "",
  item_category5: `Availability - ${exam.availability}`,
  location_id: exam.examCentreId,
  price: exam.price,
  quantity: 1,
  exam_date: exam.examDate,
});

const isNullOrEmpty = (value: string | null | undefined) =>
  value === null || value === undefined || value === "";

const filterEmptyValues = (arr: Array<string | null | undefined>) =>
  arr.filter((x) => x !== null && x !== undefined && x !== "");

const GTM = {
  pageView: (eventParams: EventParams) => {
    gtag("event", GT_EVENT.PageView, eventParams);
  },
  selectCountry: (country: SelectedCountry) => {
    gtag("event", GT_EVENT.SelectContent, {
      content_type: "country",
      content_id: country.value,
      content_label: country.label,
      content_iso_code: country.isoCode,
    });
  },
  viewExamList: (listId: string, listName: string, exams: ExamItem[]) => {
    gtag("event", GT_EVENT.ViewItemList, {
      item_list_id: listId,
      item_list_name: listName,
      currency: exams[0]?.currencyCode,
      items: exams.map((exam, index) => ({
        ...mapExamToItem(exam),
        index,
      })),
    });
  },
  addToCart: (exam: ExamItem) => {
    gtag("event", GT_EVENT.AddToCart, {
      value: exam.price,
      currency: exam.currencyCode,
      items: [mapExamToItem(exam)],
    });
  },
  beginCheckout: (exam: ExamItem) => {
    gtag("event", GT_EVENT.BeginCheckout, {
      value: exam.price,
      currency: exam.currencyCode,
      items: [mapExamToItem(exam)],
    });
  },
  addShippingInfo: (exam: ExamItem) => {
    gtag("event", GT_EVENT.AddShippingInfo, {
      value: exam.price,
      currency: exam.currencyCode,
      items: [mapExamToItem(exam)],
    });
  },
  addPaymentInfo: (exam: ExamItem, paymentVariant: "online" | "offline") => {
    gtag("event", GT_EVENT.AddPaymentInfo, {
      value: exam.price,
      currency: exam.currencyCode,
      items: [{ ...mapExamToItem(exam), payment_variant: paymentVariant }],
    });
  },
  setUserData: (details: CandidateDetails) => {
    gtag("set", "user_data", {
      email: !isNullOrEmpty(details.guardianEmail)
        ? details.guardianEmail
        : details.email,
      phone_number: filterEmptyValues([
        details.phoneNo,
        details.telephoneNo,
        details.guardianmobilenumber,
        details.guardianphonenumber,
      ]),
      address: {
        first_name: details.firstName,
        last_name: details.lastName,
        street: details.address + " " + details.addressLine2,
        city: details.city,
        postal_code: details.addressLine4,
        country: details.country.label,
      },
    });
  },
  purchase: (
    exam: ExamItem,
    transactionId: string | null,
    paymentVariant: "online" | "offline"
  ) => {
    gtag("event", GT_EVENT.Purchase, {
      transaction_id: transactionId,
      value: exam.price,
      currency: exam.currencyCode,
      items: [{ ...mapExamToItem(exam), payment_variant: paymentVariant }],
    });
  },
};

export default GTM;
